import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';

export default function Mesas() {
  const [mesas, setMesas] = useState([]);
  const [meseros, setMeseros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zonaActiva, setZonaActiva] = useState(null); // null = vista de zonas

  // Modales
  const [showModalZonas, setShowModalZonas] = useState(false);
  const [showModalNuevaMesa, setShowModalNuevaMesa] = useState(false);
  const [showAbrirModal, setShowAbrirModal] = useState(false);
  const [showConfigMesaModal, setShowConfigMesaModal] = useState(false);
  const [showEditarMesaModal, setShowEditarMesaModal] = useState(false);

  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [meseroSeleccionado, setMeseroSeleccionado] = useState('');
  const [descripcionMesa, setDescripcionMesa] = useState('');
  const [formMesa, setFormMesa] = useState({ numero: '', zona: '' });
  const [formEditarMesa, setFormEditarMesa] = useState({ numero: '', zona: '' });

  // Gestión de zonas
  const [nuevaZona, setNuevaZona] = useState('');
  const [zonaEditando, setZonaEditando] = useState(null); // { original, nuevo }

  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [mesasRes, meserosRes] = await Promise.all([
        api.get('/mesas'),
        api.get('/meseros'),
      ]);
      setMesas(mesasRes.data);
      setMeseros(meserosRes.data);
    } catch (error) {
      Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Zonas ────────────────────────────────────────────────────────────────
const getZonas = () => {
    const zonasSet = new Set();
    [...mesas]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .forEach(m => {
            if (m.zona && m.zona.trim()) zonasSet.add(m.zona.trim());
        });
    return Array.from(zonasSet);
};

  const getMesasSinZona = () => mesas.filter(m => !m.zona || !m.zona.trim());

  const getMesasPorZona = (zona) => mesas.filter(m => m.zona && m.zona.trim() === zona);

  const getResumenZona = (mesasZona) => {
    const disponibles = mesasZona.filter(m => m.estado === 'disponible').length;
    const ocupadas = mesasZona.filter(m => m.estado === 'ocupada').length;
    const pendientes = mesasZona.filter(m => m.estado === 'cuenta_pendiente').length;
    return { disponibles, ocupadas, pendientes, total: mesasZona.length };
  };

  const handleCrearZona = () => {
    if (!nuevaZona.trim()) return;
    const zonas = getZonas();
    if (zonas.map(z => z.toLowerCase()).includes(nuevaZona.trim().toLowerCase())) {
      Swal.fire('Aviso', 'Ya existe una zona con ese nombre', 'warning');
      return;
    }
    // La zona se crea "vacía" — existe cuando se asigna al menos una mesa
    // Guardamos en estado local para mostrarla de inmediato aunque esté vacía
    setMesas(prev => [...prev, { id: `zona-temp-${Date.now()}`, zona: nuevaZona.trim(), _soloZona: true }]);
    setNuevaZona('');
  };

  const handleEliminarZona = async (zona) => {
    const mesasEnZona = getMesasPorZona(zona);
    if (mesasEnZona.length > 0) {
      const result = await Swal.fire({
        title: `¿Eliminar zona "${zona}"?`,
        text: `Tiene ${mesasEnZona.length} mesa(s). Se quedará sin zona asignada.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar zona',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      });
      if (!result.isConfirmed) return;
      // Quitar zona a todas las mesas de esta zona
      for (const mesa of mesasEnZona) {
        await api.put(`/mesas/${mesa.id}`, { ...mesa, zona: '' });
      }
      await cargarDatos();
    } else {
      // Zona vacía (solo local)
      setMesas(prev => prev.filter(m => !(m._soloZona && m.zona === zona)));
    }
  };

  const handleRenombrarZona = async (zonaOriginal, zonaNew) => {
    if (!zonaNew.trim() || zonaNew.trim() === zonaOriginal) {
      setZonaEditando(null);
      return;
    }
    const mesasEnZona = getMesasPorZona(zonaOriginal);
    try {
      for (const mesa of mesasEnZona) {
        await api.put(`/mesas/${mesa.id}`, { ...mesa, zona: zonaNew.trim() });
      }
      if (zonaActiva === zonaOriginal) setZonaActiva(zonaNew.trim());
      setZonaEditando(null);
      await cargarDatos();
    } catch {
      Swal.fire('Error', 'No se pudo renombrar la zona', 'error');
    }
  };

  // ─── Mesas ────────────────────────────────────────────────────────────────
  const handleAbrirMesa = (mesa) => {
    if (meseros.length === 0) {
      Swal.fire('Aviso', 'No hay meseros registrados. Agrega un mesero primero.', 'warning');
      return;
    }
    setMesaSeleccionada(mesa);
    setMeseroSeleccionado('');
    setDescripcionMesa('');
    setShowAbrirModal(true);
  };

  const handleVerPedido = async (mesa) => {
    try {
      const res = await api.get(`/ordenes/mesa/${mesa.id}`);
      navigate(`/ordenes/${res.data.id}`);
    } catch {
      Swal.fire('Error', 'No se pudo encontrar la orden activa', 'error');
    }
  };

  const confirmarAbrirMesa = async () => {
    if (!meseroSeleccionado) {
      Swal.fire('Error', 'Selecciona un mesero', 'error');
      return;
    }
    try {
      const res = await api.post('/ordenes', {
        mesa_id: mesaSeleccionada.id,
        mesero_id: meseroSeleccionado,
        descripcion: descripcionMesa,
      });
      setShowAbrirModal(false);
      navigate(`/ordenes/${res.data.id}`);
    } catch {
      Swal.fire('Error', 'No se pudo abrir la mesa', 'error');
    }
  };

  const handleGuardarNuevaMesa = async () => {
    if (!formMesa.numero) {
      Swal.fire('Error', 'El número de mesa es requerido', 'error');
      return;
    }
    try {
      await api.post('/mesas', { ...formMesa, zona: zonaActiva || formMesa.zona || '' });
      Swal.fire('Éxito', 'Mesa creada correctamente', 'success');
      setShowModalNuevaMesa(false);
      setFormMesa({ numero: '', zona: '' });
      cargarDatos();
    } catch {
      Swal.fire('Error', 'No se pudo crear la mesa', 'error');
    }
  };

  const handleEditarMesa = async () => {
    if (!formEditarMesa.numero) {
      Swal.fire('Error', 'El número de mesa es requerido', 'error');
      return;
    }
    try {
      await api.put(`/mesas/${mesaSeleccionada.id}`, formEditarMesa);
      Swal.fire('Éxito', 'Mesa actualizada', 'success');
      setShowEditarMesaModal(false);
      setShowConfigMesaModal(false);
      cargarDatos();
    } catch {
      Swal.fire('Error', 'No se pudo actualizar la mesa', 'error');
    }
  };

  const handleEliminarMesa = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar mesa?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/mesas/${id}`);
        Swal.fire('Éxito', 'Mesa eliminada correctamente', 'success');
        setShowConfigMesaModal(false);
        cargarDatos();
      } catch {
        Swal.fire('Error', 'No se pudo eliminar la mesa', 'error');
      }
    }
  };

  // ─── Helpers visuales ─────────────────────────────────────────────────────
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'disponible': return '#28a745';
      case 'ocupada': return '#dc3545';
      case 'cuenta_pendiente': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'disponible': return 'DISPONIBLE';
      case 'ocupada': return 'OCUPADA';
      case 'cuenta_pendiente': return 'CUENTA PENDIENTE';
      default: return estado;
    }
  };

  const getIconoZona = (zona) => {
    const z = zona.toLowerCase();
    if (z.includes('sala')) return '🪑';
    if (z.includes('patio') || z.includes('exterior') || z.includes('terraza')) return '🌿';
    if (z.includes('planta') || z.includes('piso')) return '🏢';
    if (z.includes('vip') || z.includes('privado')) return '⭐';
    if (z.includes('familiar') || z.includes('familia')) return '👨‍👩‍👧';
    if (z.includes('bar')) return '🍹';
    return '📍';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const zonas = getZonas();
  const mesasSinZona = getMesasSinZona().filter(m => !m._soloZona);

  // ─── VISTA INTERIOR DE ZONA ───────────────────────────────────────────────
  if (zonaActiva !== null) {
    const essinZona = zonaActiva === '__sin_zona__';
    const mesasDeZona = essinZona ? mesasSinZona : getMesasPorZona(zonaActiva).filter(m => !m._soloZona);
    const nombreZona = essinZona ? 'Sin zona asignada' : zonaActiva;

    return (
      <div className="p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
  <div className="d-flex align-items-center gap-2 flex-wrap">
    <button
      className="btn btn-outline-secondary btn-sm"
      onClick={() => setZonaActiva(null)}
    >
      ← Volver
    </button>
    <h5 className="mb-0">
      {!essinZona && <span className="me-1">{getIconoZona(zonaActiva)}</span>}
      {nombreZona}
    </h5>
    <span className="badge bg-secondary">{mesasDeZona.length} mesas</span>
  </div>
  <button
    className="btn btn-primary btn-sm"
    onClick={() => {
      setFormMesa({ numero: '', zona: essinZona ? '' : zonaActiva });
      setShowModalNuevaMesa(true);
    }}
  >
    + Nueva Mesa
  </button>
</div>

        {/* Mesas de la zona */}
        {mesasDeZona.length === 0 ? (
          <div className="text-center text-muted mt-5">
            <div style={{ fontSize: 48 }}>🍽️</div>
            <h5 className="mt-3">No hay mesas en esta zona</h5>
            <p>Agrega la primera mesa con el botón "Nueva Mesa"</p>
          </div>
        ) : (
          <div className="row g-3">
            {mesasDeZona.map((mesa) => (
              <div key={mesa.id} className="col-6 col-md-4 col-lg-3">
                <div
                  className="card h-100 text-center"
                  style={{
                    borderTop: `4px solid ${getEstadoColor(mesa.estado)}`,
                    borderRadius: '12px',
                    position: 'relative',
                  }}
                >
                  <button
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      background: 'none', border: 'none',
                      fontSize: '18px', cursor: 'pointer',
                      color: '#6c757d', padding: '2px 6px',
                      borderRadius: '50%',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMesaSeleccionada(mesa);
                      setFormEditarMesa({ numero: mesa.numero, zona: mesa.zona || '' });
                      setShowConfigMesaModal(true);
                    }}
                    title="Configurar mesa"
                  >
                    ⚙️
                  </button>
                  <div className="card-body">
                    <h3 className="card-title fw-bold">Mesa {mesa.numero}</h3>
                    <span className="badge mb-3" style={{ backgroundColor: getEstadoColor(mesa.estado) }}>
                      {getEstadoTexto(mesa.estado)}
                    </span>
                    <div className="d-flex flex-column gap-2 align-items-center">
                      {mesa.estado === 'ocupada' || mesa.estado === 'cuenta_pendiente' ? (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleVerPedido(mesa)}
                        >
                          👁 Ver Pedido
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleAbrirMesa(mesa)}
                        >
                          Abrir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Config Mesa */}
        {showConfigMesaModal && mesaSeleccionada && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">⚙️ Mesa {mesaSeleccionada.numero}</h5>
                  <button className="btn-close" onClick={() => setShowConfigMesaModal(false)} />
                </div>
                <div className="modal-body d-flex flex-column gap-2">
                  <button
                    className="btn btn-outline-primary w-100"
                    onClick={() => { setShowConfigMesaModal(false); setShowEditarMesaModal(true); }}
                  >
                    ✏️ Editar Mesa
                  </button>
                  <button
                    className="btn btn-danger w-100"
                    onClick={() => handleEliminarMesa(mesaSeleccionada.id)}
                  >
                    🗑️ Eliminar Mesa
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Editar Mesa */}
        {showEditarMesaModal && mesaSeleccionada && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">✏️ Editar Mesa {mesaSeleccionada.numero}</h5>
                  <button className="btn-close" onClick={() => setShowEditarMesaModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Número de Mesa *</label>
                    <input
                      type="text" className="form-control"
                      value={formEditarMesa.numero}
                      onChange={(e) => setFormEditarMesa({ ...formEditarMesa, numero: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Zona</label>
                    <select
                      className="form-select"
                      value={formEditarMesa.zona}
                      onChange={(e) => setFormEditarMesa({ ...formEditarMesa, zona: e.target.value })}
                    >
                      <option value="">Sin zona</option>
                      {zonas.map(z => (
                        <option key={z} value={z}>{z}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowEditarMesaModal(false)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleEditarMesa}>Guardar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Abrir Mesa */}
        {showAbrirModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Abrir Mesa {mesaSeleccionada?.numero}</h5>
                  <button className="btn-close" onClick={() => setShowAbrirModal(false)} />
                </div>
                <div className="modal-body">
  <div className="mb-3">
    <label className="form-label">Selecciona el mesero *</label>
    <select className="form-select" value={meseroSeleccionado} onChange={(e) => setMeseroSeleccionado(e.target.value)}>
      <option value="">-- Seleccionar mesero --</option>
      {meseros.map((m) => (
        <option key={m.id} value={m.id}>{m.nombre}</option>
      ))}
    </select>
  </div>
  <div className="mb-3">
    <label className="form-label">Descripción (opcional)</label>
    <input
      type="text"
      className="form-control"
      placeholder="Ej: Mesa de Raúl"
      value={descripcionMesa}
      onChange={(e) => setDescripcionMesa(e.target.value)}
    />
  </div>
</div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowAbrirModal(false)}>Cancelar</button>
                  <button className="btn btn-success" onClick={confirmarAbrirMesa}>Abrir Mesa</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Nueva Mesa */}
        {showModalNuevaMesa && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Nueva Mesa en {nombreZona}</h5>
                  <button className="btn-close" onClick={() => setShowModalNuevaMesa(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Número de Mesa *</label>
                    <input
                      type="text" className="form-control"
                      placeholder="Ej: 1, 2, VIP1"
                      value={formMesa.numero}
                      onChange={(e) => setFormMesa({ ...formMesa, numero: e.target.value })}
                    />
                  </div>
                  {essinZona && (
                    <div className="mb-3">
                      <label className="form-label">Zona (opcional)</label>
                      <select
                        className="form-select"
                        value={formMesa.zona}
                        onChange={(e) => setFormMesa({ ...formMesa, zona: e.target.value })}
                      >
                        <option value="">Sin zona</option>
                        {zonas.map(z => (
                          <option key={z} value={z}>{z}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModalNuevaMesa(false)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleGuardarNuevaMesa}>Guardar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── VISTA PRINCIPAL DE ZONAS ─────────────────────────────────────────────
return (
  <div className="p-4">
    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <h2 className="mb-0">Mesas</h2>
      <div className="d-flex gap-2 flex-wrap">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowModalZonas(true)}>
          🗂️ Gestionar Zonas
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setFormMesa({ numero: '', zona: zonas[0] || '' });
            setShowModalNuevaMesa(true);
          }}
        >
          + Nueva Mesa
        </button>
      </div>
    </div>

    {zonas.length === 0 && mesasSinZona.length === 0 ? (
        <div className="text-center text-muted mt-5">
          <div style={{ fontSize: 56 }}>🍽️</div>
          <h5 className="mt-3">No hay zonas ni mesas registradas</h5>
          <p>Empieza creando una zona con el botón "Gestionar Zonas"</p>
        </div>
      ) : (
        <div className="row g-4">
          {/* Tarjetas de zonas */}
          {[...zonas, ...(mesas.filter(m => m._soloZona).map(m => m.zona))].filter((v, i, a) => a.indexOf(v) === i).map((zona) => {
            const mesasZona = getMesasPorZona(zona).filter(m => !m._soloZona);
            const { disponibles, ocupadas, pendientes, total } = getResumenZona(mesasZona);
            const hayOcupadas = ocupadas > 0 || pendientes > 0;

            return (
              <div key={zona} className="col-12 col-md-6 col-lg-4">
                <div
                  className="card h-100"
                  style={{
                    borderRadius: '16px',
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  onClick={() => setZonaActiva(zona)}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span style={{ fontSize: 32 }}>{getIconoZona(zona)}</span>
                        <h4 className="fw-bold mt-1 mb-0">{zona}</h4>
                        <small className="text-muted">{total} mesa{total !== 1 ? 's' : ''}</small>
                      </div>
                      {hayOcupadas && (
                        <span className="badge bg-danger" style={{ fontSize: '0.75rem' }}>
                          {ocupadas + pendientes} ocupada{(ocupadas + pendientes) !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Barra de estado */}
                    {total > 0 && (
                      <div className="mb-3">
                        <div className="progress" style={{ height: 8, borderRadius: 8 }}>
                          <div
                            className="progress-bar bg-success"
                            style={{ width: `${(disponibles / total) * 100}%` }}
                          />
                          <div
                            className="progress-bar bg-danger"
                            style={{ width: `${(ocupadas / total) * 100}%` }}
                          />
                          <div
                            className="progress-bar bg-warning"
                            style={{ width: `${(pendientes / total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="d-flex gap-3">
                      <span className="text-success fw-semibold">✔ {disponibles} libre{disponibles !== 1 ? 's' : ''}</span>
                      {ocupadas > 0 && <span className="text-danger fw-semibold">● {ocupadas} ocupada{ocupadas !== 1 ? 's' : ''}</span>}
                      {pendientes > 0 && <span className="text-warning fw-semibold">⏳ {pendientes} pendiente{pendientes !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Tarjeta mesas sin zona */}
          {mesasSinZona.length > 0 && (
            <div className="col-12 col-md-6 col-lg-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: '16px',
                  border: '1px dashed #aaa',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  backgroundColor: '#fafafa',
                }}
                onClick={() => setZonaActiva('__sin_zona__')}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="card-body p-4">
                  <span style={{ fontSize: 32 }}>📦</span>
                  <h4 className="fw-bold mt-1 mb-0">Sin zona</h4>
                  <small className="text-muted">{mesasSinZona.length} mesa{mesasSinZona.length !== 1 ? 's' : ''} sin asignar</small>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modal Gestionar Zonas ── */}
      {showModalZonas && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🗂️ Gestionar Zonas</h5>
                <button className="btn-close" onClick={() => { setShowModalZonas(false); setZonaEditando(null); setNuevaZona(''); }} />
              </div>
              <div className="modal-body">
                {/* Crear nueva zona */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Nueva zona</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: Terraza, Barra, VIP..."
                      value={nuevaZona}
                      onChange={(e) => setNuevaZona(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCrearZona()}
                    />
                    <button className="btn btn-primary" onClick={handleCrearZona}>
                      Agregar
                    </button>
                  </div>
                </div>

                {/* Lista de zonas */}
                <label className="form-label fw-semibold">Zonas existentes</label>
                {[...zonas, ...(mesas.filter(m => m._soloZona).map(m => m.zona))].filter((v, i, a) => a.indexOf(v) === i).length === 0 ? (
                  <p className="text-muted">Aún no hay zonas. Agrega la primera arriba.</p>
                ) : (
                  <ul className="list-group">
                    {[...zonas, ...(mesas.filter(m => m._soloZona).map(m => m.zona))].filter((v, i, a) => a.indexOf(v) === i).map((zona) => {
                      const count = getMesasPorZona(zona).filter(m => !m._soloZona).length;
                      const editando = zonaEditando?.original === zona;
                      return (
                        <li key={zona} className="list-group-item d-flex align-items-center gap-2">
                          <span className="me-1">{getIconoZona(zona)}</span>
                          {editando ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={zonaEditando.nuevo}
                              autoFocus
                              onChange={(e) => setZonaEditando({ ...zonaEditando, nuevo: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenombrarZona(zona, zonaEditando.nuevo);
                                if (e.key === 'Escape') setZonaEditando(null);
                              }}
                            />
                          ) : (
                            <span className="flex-grow-1">
                              {zona} <small className="text-muted">({count} mesa{count !== 1 ? 's' : ''})</small>
                            </span>
                          )}
                          <div className="d-flex gap-1 ms-auto">
                            {editando ? (
                              <>
                                <button className="btn btn-sm btn-success" onClick={() => handleRenombrarZona(zona, zonaEditando.nuevo)}>✔</button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setZonaEditando(null)}>✕</button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  title="Renombrar"
                                  onClick={() => setZonaEditando({ original: zona, nuevo: zona })}
                                >✏️</button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="Eliminar zona"
                                  onClick={() => handleEliminarZona(zona)}
                                >🗑️</button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowModalZonas(false); setZonaEditando(null); setNuevaZona(''); }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nueva Mesa (desde vista principal) */}
      {showModalNuevaMesa && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nueva Mesa</h5>
                <button className="btn-close" onClick={() => setShowModalNuevaMesa(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Número de Mesa *</label>
                  <input
                    type="text" className="form-control"
                    placeholder="Ej: 1, 2, VIP1"
                    value={formMesa.numero}
                    onChange={(e) => setFormMesa({ ...formMesa, numero: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Zona</label>
                  <select
                    className="form-select"
                    value={formMesa.zona}
                    onChange={(e) => setFormMesa({ ...formMesa, zona: e.target.value })}
                  >
                    <option value="">Sin zona</option>
                    {zonas.map(z => (
                      <option key={z} value={z}>{z}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModalNuevaMesa(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleGuardarNuevaMesa}>Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}